import { deleteData, getData, postData, putData } from "../api/api";
import sessionAuthApi from "./sessionAuth";

export type OpenSearchMode = "BEAS_MANAGED" | "EXTERNAL";
export type LlmMode = "BEAS_MANAGED" | "EXTERNAL";

export type BeasLlmModelOption = {
  id: string;
  name: string;
};

export type WorkspaceSettings = {
  workspaceId: string;
  workspaceName: string;
  llmProvider: "OPENAI" | "OLLAMA" | "OPENAI_COMPATIBLE";
  llmMode?: LlmMode | null;
  beasLlmModelId?: string | null;
  beasLlmModelName?: string | null;
  llmBaseUrl?: string;
  llmApiKeySet: boolean;
  chatModel?: string;
  embeddingModel?: string;
  openSearchMode?: OpenSearchMode | null;
  openSearchUrl?: string;
  openSearchUsername?: string;
  openSearchPasswordSet: boolean;
  openSearchSslInsecure: boolean;
  openSearchIndexPrefix?: string | null;
  beasProvisioned?: boolean;
  allowedIndices?: string[];
  /** Index-candidate in-memory cache TTL in minutes (1–60). */
  indexCandidateCacheMinutes?: number;
  llmOk: boolean;
  openSearchOk: boolean;
  setupComplete: boolean;
};

export type WorkspaceSettingsPayload = {
  llmProvider?: string;
  llmMode?: LlmMode;
  beasLlmModelId?: string | null;
  llmBaseUrl?: string;
  llmApiKey?: string;
  chatModel?: string;
  embeddingModel?: string;
  openSearchMode?: OpenSearchMode;
  openSearchUrl?: string;
  openSearchUsername?: string;
  openSearchPassword?: string;
  openSearchSslInsecure?: boolean;
  allowedIndices?: string[];
  indexCandidateCacheMinutes?: number;
};

export type HomeLimitUsage = { key: string; used: number; max: number | null };

/** Role-filtered welcome snapshot; a section the caller may not see is null/absent. */
export type WorkspaceHomeSummary = {
  workspaceName?: string | null;
  content?: {
    dashboards: number;
    widgets: number;
    dashboardQueries: number;
    recentDashboards: { id: string; name: string; published: boolean; updatedAt?: string | null }[];
  } | null;
  limits?: { packageType: string; unlimited: boolean; items: HomeLimitUsage[] } | null;
  anomalies?: {
    openIncidents: number;
    acknowledgedIncidents: number;
    recent: {
      id: string;
      definitionName?: string;
      status?: string;
      grade?: string;
      score: number;
      lastSeenAt?: string | null;
    }[];
  } | null;
  dataPipeline?: {
    ingestCompleted7d: number;
    ingestFailed7d: number;
    prepJobs: number;
    prepFailedRuns7d: number;
    pendingIndexApprovals?: number | null;
  } | null;
  connections?: { dataAdaptors: number; openApiCatalogs: number } | null;
  schedules?: { activeExportSchedules: number; enabledInsightJobs: number; failing: number } | null;
  health?: {
    businessErrors24h: number;
    setupComplete?: boolean | null;
    llmOk?: boolean | null;
    openSearchOk?: boolean | null;
  } | null;
};

const BASE = "/workspaces";

export const workspaceService = {
  me: () => getData(`${BASE}/me`),
  homeSummary: () =>
    getData(`${BASE}/me/summary`, { skipGlobalLoading: true }) as Promise<WorkspaceHomeSummary>,
  getSettings: () => getData(`${BASE}/me/settings`) as Promise<WorkspaceSettings>,
  saveSettings: (payload: WorkspaceSettingsPayload) =>
    putData(`${BASE}/me/settings`, payload) as Promise<WorkspaceSettings>,
  testLlm: () => postData(`${BASE}/me/settings/test-llm`, {}) as Promise<{ ok: boolean }>,
  testOpenSearch: () =>
    postData(`${BASE}/me/settings/test-opensearch`, {}) as Promise<{ ok: boolean }>,
  provisionBeasOpenSearch: () =>
    postData(`${BASE}/me/settings/opensearch/beas`, {}) as Promise<WorkspaceSettings>,
  listBeasLlmModels: () =>
    getData(`${BASE}/me/settings/beas-llm-models`) as Promise<BeasLlmModelOption[]>,
  listBeasLlmModelsById: (workspaceId: string) =>
    getData(`${BASE}/${workspaceId}/settings/beas-llm-models`) as Promise<BeasLlmModelOption[]>,
  selectBeasLlm: (modelId: string) =>
    postData(`${BASE}/me/settings/llm/beas`, { modelId }) as Promise<WorkspaceSettings>,
  selectBeasLlmById: (workspaceId: string, modelId: string) =>
    postData(`${BASE}/${workspaceId}/settings/llm/beas`, { modelId }) as Promise<WorkspaceSettings>,
  setupStatus: () =>
    getData(`${BASE}/me/setup-status`) as Promise<{
      setupComplete: boolean;
      llmOk: boolean;
      openSearchOk: boolean;
    }>,
  invite: (email: string, role: WorkspaceRole = "USER", workspaceId?: string) =>
    postData(workspaceId ? `${BASE}/${workspaceId}/invites` : `${BASE}/invites`, { email, role }),
  listInvites: () => getData(`${BASE}/invites`),
  acceptInvite: async (token: string) => {
    const result = await postData(`${BASE}/invites/${token}/accept`, {});
    await sessionAuthApi.refresh();
    return result;
  },
  listMembers: (workspaceId?: string) =>
    getData(workspaceId ? `${BASE}/${workspaceId}/members` : `${BASE}/members`) as Promise<WorkspaceMember[]>,
  removeMember: (userId: string, workspaceId?: string) =>
    deleteData(workspaceId ? `${BASE}/${workspaceId}/members/${userId}` : `${BASE}/members/${userId}`),
  listAll: () => getData(BASE) as Promise<WorkspaceSummary[]>,
  updateWorkspace: (workspaceId: string, payload: { name: string }) =>
    putData(`${BASE}/${workspaceId}`, payload) as Promise<WorkspaceSummary>,
  getSettingsById: (workspaceId: string) =>
    getData(`${BASE}/${workspaceId}/settings`) as Promise<WorkspaceSettings>,
  saveSettingsById: (workspaceId: string, payload: WorkspaceSettingsPayload) =>
    putData(`${BASE}/${workspaceId}/settings`, payload) as Promise<WorkspaceSettings>,
  testLlmById: (workspaceId: string) =>
    postData(`${BASE}/${workspaceId}/settings/test-llm`, {}) as Promise<{ ok: boolean }>,
  testOpenSearchById: (workspaceId: string) =>
    postData(`${BASE}/${workspaceId}/settings/test-opensearch`, {}) as Promise<{ ok: boolean }>,
  provisionBeasOpenSearchById: (workspaceId: string) =>
    postData(`${BASE}/${workspaceId}/settings/opensearch/beas`, {}) as Promise<WorkspaceSettings>,
};

export type WorkspaceSummary = {
  id: string;
  name: string;
  slug?: string;
  ownerUsername?: string;
  setupComplete: boolean;
  llmOk: boolean;
  openSearchOk: boolean;
};

export type WorkspaceRole = "TENANT_ADMIN" | "USER" | "REPORTER" | "VIEWER";

export type WorkspaceMember = {
  userId: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role: WorkspaceRole;
  owner: boolean;
  self: boolean;
};
